import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { FetchError, useFetch } from '@/modules/core/composables/useFetch'
import type {
  ApplicationChoice,
  ApplicationHeading,
  ApplicationQuestion,
  ApplicationTemplate,
  QuestionType,
} from '../types'

// Every command refetches the template afterwards: the builder payload is the
// single source of truth and stays small enough that this beats patching
// nested state by hand.
export const useBuilderStore = defineStore('applications/builderStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/v4/${route.params.slug}/applications`)

  const template = ref<ApplicationTemplate | null>(null)
  const loading = ref(false)
  const notFound = ref(false)
  const error = ref(false)

  const fetchTemplate = async (templateId: number): Promise<void> => {
    // Only the initial load (or a template switch) toggles `loading`: background
    // refetches after each command must not unmount the builder, or the page
    // collapses, scrolls to the top, and the focused field is lost.
    const isInitialLoad = template.value?.id !== templateId
    if (isInitialLoad) {
      loading.value = true
      notFound.value = false
      error.value = false
    }
    try {
      template.value = await useFetch(`${baseUrl.value}/templates/${templateId}`).get<ApplicationTemplate>()
    } catch (e) {
      // a failed background refetch keeps the last known template on screen
      if (!isInitialLoad) return

      notFound.value = e instanceof FetchError && e.status === 404
      error.value = !(e instanceof FetchError && e.status === 404)
    } finally {
      if (isInitialLoad) loading.value = false
    }
  }

  const refetch = () => fetchTemplate(template.value?.id ?? 0)

  // commands are a no-op without a loaded template — the page only renders
  // them when one exists, but the store API must not build garbage URLs
  const hasTemplate = () => template.value !== null

  const renameTemplate = async (name: string): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/update`).post({ name }, { silent: false })
    await refetch()
  }

  const publish = async (): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/publish`).post(undefined, { silent: false })
    await refetch()
  }

  const draft = async (): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/draft`).post(undefined, { silent: false })
    await refetch()
  }

  const archive = async (): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/archive`).post(undefined, { silent: false })
    await refetch()
  }

  const createHeading = async (name: string, description?: string): Promise<ApplicationHeading> => {
    if (!hasTemplate()) throw new Error('template is not loaded')
    const heading = await useFetch(
      `${baseUrl.value}/templates/${template.value!.id}/headings`,
    ).post<ApplicationHeading>({ name, description }, { silent: false })
    await refetch()
    return heading
  }

  const updateHeading = async (headingId: number, name: string, description: string): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/headings/${headingId}/update`).post(
      { name, description },
      { silent: false },
    )
    await refetch()
  }

  const destroyHeading = async (headingId: number): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/headings/${headingId}/destroy`).post(undefined, {
      silent: false,
    })
    await refetch()
  }

  const sortHeadings = async (headingIds: number[]): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/headings/sort`).post(
      { heading_ids: headingIds },
      { silent: false },
    )
    await refetch()
  }

  // position is the 1-based insertion slot (button above the list sends 1, the
  // button after question N sends N+1); the API splices the question in and
  // rewrites the heading's numbering. Omitted position appends.
  const createQuestion = async (
    headingId: number,
    name: string,
    type: QuestionType,
    position?: number,
  ): Promise<ApplicationQuestion> => {
    if (!hasTemplate()) throw new Error('template is not loaded')
    const question = await useFetch(
      `${baseUrl.value}/templates/${template.value!.id}/headings/${headingId}/questions`,
    ).post<ApplicationQuestion>({ name, type, position }, { silent: false })
    await refetch()
    return question
  }

  const updateQuestion = async (questionId: number, name: string, description: string): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/questions/${questionId}/update`).post(
      { name, description },
      { silent: false },
    )
    await refetch()
  }

  const destroyQuestion = async (questionId: number): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/questions/${questionId}/destroy`).post(undefined, {
      silent: false,
    })
    await refetch()
  }

  const sortQuestions = async (headingId: number, questionIds: number[]): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/headings/${headingId}/questions/sort`).post(
      { question_ids: questionIds },
      { silent: false },
    )
    await refetch()
  }

  const createChoice = async (questionId: number, description: string, value: number): Promise<ApplicationChoice> => {
    if (!hasTemplate()) throw new Error('template is not loaded')
    const choice = await useFetch(
      `${baseUrl.value}/templates/${template.value!.id}/questions/${questionId}/choices`,
    ).post<ApplicationChoice>({ description, value }, { silent: false })
    await refetch()
    return choice
  }

  const updateChoice = async (choiceId: number, description: string, value: number): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/choices/${choiceId}/update`).post(
      { description, value },
      { silent: false },
    )
    await refetch()
  }

  const destroyChoice = async (choiceId: number): Promise<void> => {
    if (!hasTemplate()) return
    await useFetch(`${baseUrl.value}/templates/${template.value!.id}/choices/${choiceId}/destroy`).post(undefined, {
      silent: false,
    })
    await refetch()
  }

  const reset = (): void => {
    template.value = null
    notFound.value = false
    error.value = false
  }

  return {
    template,
    loading,
    notFound,
    error,
    fetchTemplate,
    renameTemplate,
    publish,
    draft,
    archive,
    createHeading,
    updateHeading,
    destroyHeading,
    sortHeadings,
    createQuestion,
    updateQuestion,
    destroyQuestion,
    sortQuestions,
    createChoice,
    updateChoice,
    destroyChoice,
    reset,
  }
})
